import { schemaDocument, schemaFields } from './schema';
import { CanonicalSchema, FieldMapping } from './types';

const XPATH_FN = 'http://www.w3.org/2005/xpath-functions';

const escapeText = (value: string): string => value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const escapeAttribute = (value: string): string => escapeText(value).replace(/"/g, '&quot;');

const escapeXpathLiteral = (value: string): string => `'${value.replace(/'/g, "''")}'`;

const segments = (path: string): string[] => path.split('.').filter(Boolean);

/**
 * A source path into the body Camel hands the stylesheet. With
 * `xslt-saxon:...?useJsonBody=true` the context item is already the JSON in the XPath
 * functions namespace, so a field is addressed by its container element and @key rather
 * than by parsing the body again.
 */
const sourceSelect = (path: string): string => {
  const parts = segments(path);
  if (parts.length === 0) return '.';

  return parts.reduce((select, part) => {
    if (/^\d+$/.test(part)) {
      return `${select}/*[${Number(part) + 1}]`;
    }

    // any container: an intermediate segment may be a map or an array
    return `${select}/*[@key=${escapeXpathLiteral(part)}]`;
  }, '');
};

const valueSelect = (mapping: FieldMapping): string => {
  if (mapping.sourceKind === 'constant') return escapeXpathLiteral(mapping.source);
  if (mapping.sourceKind === 'field') return `/fn:map${sourceSelect(mapping.source)}`;

  const pieces = mapping.source.split(/\{([^}]+)\}/g).flatMap((piece, index) => {
    if (index % 2 === 1) return [`string(/fn:map${sourceSelect(piece.trim())})`];
    return piece === '' ? [] : [escapeXpathLiteral(piece)];
  });

  if (pieces.length === 0) return "''";

  return pieces.length === 1 ? pieces[0] : `concat(${pieces.join(', ')})`;
};

type Tree = Map<string, Tree | FieldMapping>;

const buildTree = (mappings: FieldMapping[]): Tree => {
  const root: Tree = new Map();

  mappings
    .filter((mapping) => mapping.targetPath.trim() !== '')
    .forEach((mapping) => {
      const parts = segments(mapping.targetPath);
      let cursor = root;
      parts.slice(0, -1).forEach((part) => {
        const existing = cursor.get(part);
        if (existing instanceof Map) {
          cursor = existing;
        } else {
          const branch: Tree = new Map();
          cursor.set(part, branch);
          cursor = branch;
        }
      });
      cursor.set(parts[parts.length - 1], mapping);
    });

  return root;
};

const elementFor = (type: string | undefined): 'string' | 'number' | 'boolean' | 'array' | 'map' => {
  if (type === 'number' || type === 'integer') return 'number';
  if (type === 'boolean') return 'boolean';
  if (type === 'array') return 'array';
  if (type === 'object') return 'map';

  return 'string';
};

/** Invalid numbers become null so canonical validation can name the offending field. */
const numericLeaf = (key: string, select: string, indent: string): string[] => {
  const value = escapeAttribute(select);
  const attribute = ` key="${escapeAttribute(key)}"`;
  return [
    `${indent}<xsl:choose>`,
    `${indent}  <xsl:when test="abs(number(${value})) &lt; xs:double('INF')">`,
    `${indent}    <number${attribute}><xsl:value-of select="number(${value})"/></number>`,
    `${indent}  </xsl:when>`,
    `${indent}  <xsl:otherwise><null${attribute}/></xsl:otherwise>`,
    `${indent}</xsl:choose>`,
  ];
};

const renderLeaf = (
  key: string,
  mapping: FieldMapping,
  schema: CanonicalSchema | undefined,
  indent: string,
): string[] => {
  const field = schema?.fields.find((candidate) => candidate.path === mapping.targetPath);
  const element = elementFor(field?.type);
  const select = valueSelect(mapping);
  const attribute = ` key="${escapeAttribute(key)}"`;

  if (element === 'number') return numericLeaf(key, select, indent);
  const itemFields =
    schema?.fields.filter(
      (candidate) =>
        candidate.path.startsWith(`${mapping.targetPath}[].`) &&
        !candidate.path.slice(mapping.targetPath.length + 3).includes('.'),
    ) ?? [];
  if (element === 'array' && itemFields.length) {
    return [
      `${indent}<array${attribute}>`,
      `${indent}  <xsl:for-each select="${escapeAttribute(select)}/*">`,
      `${indent}    <map>`,
      ...itemFields.flatMap((item) => {
        const name = item.path.slice(mapping.targetPath.length + 3);
        const relative = `*[@key=${escapeXpathLiteral(name)}]`;
        const tag = elementFor(item.type);
        const lines =
          tag === 'number'
            ? numericLeaf(name, relative, `${indent}      `)
            : [
                `${indent}      <${tag} key="${escapeAttribute(name)}"><xsl:value-of select="${escapeAttribute(relative)}"/></${tag}>`,
              ];
        return item.required && tag === 'number'
          ? lines
          : [
              `${indent}      <xsl:if test="exists(${escapeAttribute(relative)})">`,
              ...lines,
              `${indent}      </xsl:if>`,
            ];
      }),
      `${indent}    </map>`,
      `${indent}  </xsl:for-each>`,
      `${indent}</array>`,
    ];
  }
  if (element === 'array' || element === 'map') {
    return [
      `${indent}<xsl:for-each select="${escapeAttribute(select)}">`,
      `${indent}  <${element}${attribute}>`,
      `${indent}    <xsl:copy-of select="node()"/>`,
      `${indent}  </${element}>`,
      `${indent}</xsl:for-each>`,
    ];
  }

  return [
    `${indent}<${element}${attribute}>`,
    `${indent}  <xsl:value-of select="${escapeAttribute(select)}"/>`,
    `${indent}</${element}>`,
  ];
};

const renderTree = (tree: Tree, schema: CanonicalSchema | undefined, indent: string): string[] =>
  [...tree.entries()].flatMap(([key, node]) => {
    if (node instanceof Map) {
      return [
        `${indent}<map key="${escapeAttribute(key)}">`,
        ...renderTree(node, schema, `${indent}  `),
        `${indent}</map>`,
      ];
    }

    return renderLeaf(key, node, schema, indent);
  });

export interface GeneratedXslt {
  source: string;
  mappingCount: number;
}

/**
 * Turns the mappings a person drew in the console into the stylesheet the data plane runs.
 *
 * The emitted shape matches what Kaoto's DataMapper produces and what the plane's
 * `xslt-saxon:...?useJsonBody=true` endpoint expects: a `$mapped-xml` variable holding the
 * canonical document in the XPath functions namespace, serialised once with `xml-to-json`.
 */
export const generateXslt = (mappings: FieldMapping[], schema?: CanonicalSchema): GeneratedXslt => {
  if (schema?.jsonSchema) schema = { ...schema, fields: schemaFields(schemaDocument(schema)!) };
  const usable = mappings.filter((mapping) => mapping.targetPath.trim() !== '');
  const body = renderTree(buildTree(usable), schema, '            ');

  const source = [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<!-- Generated from the console mapping. Do not edit. -->',
    '<xsl:stylesheet xmlns:xsl="http://www.w3.org/1999/XSL/Transform" version="3.0"' +
      ' xmlns:xs="http://www.w3.org/2001/XMLSchema"' +
      ` xmlns:fn="${XPATH_FN}" exclude-result-prefixes="xs fn">`,
    '    <xsl:output method="text" indent="yes"/>',
    '    <xsl:variable name="mapped-xml">',
    `        <map xmlns="${XPATH_FN}">`,
    ...body,
    '        </map>',
    '    </xsl:variable>',
    '    <xsl:template match="/">',
    '        <xsl:value-of select="xml-to-json($mapped-xml)"/>',
    '    </xsl:template>',
    '</xsl:stylesheet>',
    '',
  ].join('\n');

  return { source, mappingCount: usable.length };
};

/** An endpoint that passes the payload through: the vendor's document, unchanged. */
export const PASS_THROUGH_XSLT = [
  '<?xml version="1.0" encoding="UTF-8"?>',
  '<!-- The endpoint passes the payload through. Do not edit. -->',
  '<xsl:stylesheet xmlns:xsl="http://www.w3.org/1999/XSL/Transform" version="3.0">',
  '    <xsl:output method="text"/>',
  '    <xsl:template match="/">',
  '        <xsl:value-of select="xml-to-json(/*)"/>',
  '    </xsl:template>',
  '</xsl:stylesheet>',
  '',
].join('\n');

/** The stylesheet a scope runs: its own when it has one, else the payload as it came on a pass-through endpoint. */
/** A stylesheet whose output is an empty object: the DataMapper saves one for a page nothing was drawn on. */
export const mapsNothing = (xslt: string): boolean =>
  /<xsl:variable name="mapped-xml">\s*<map xmlns="[^"]*"\s*(\/>|>\s*<\/map>)\s*<\/xsl:variable>/.test(xslt);

/** Whether a scope drew a mapping of its own, as opposed to an empty DataMapper page. */
export const drawsMapping = (xslt: string | undefined, mappings: FieldMapping[]): boolean =>
  xslt ? !mapsNothing(xslt) : mappings.some((mapping) => mapping.targetPath.trim());

export const stylesheetFor = (
  passThrough: boolean | undefined,
  xslt: string | undefined,
  mappings: FieldMapping[],
  schema?: CanonicalSchema,
): string =>
  passThrough && !drawsMapping(xslt, mappings) ? PASS_THROUGH_XSLT : (xslt ?? generateXslt(mappings, schema).source);

export const sha256Hex = async (value: string): Promise<string> => {
  const bytes = new TextEncoder().encode(value);
  const digest = await crypto.subtle.digest('SHA-256', bytes);

  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
};
